import React, { useEffect } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { createStructuredSelector } from "reselect";
import {
  submitSuspension,
  fetchSuspensionPaymentDetails,
  submitCancelSuspension
} from "ducks/actions";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import styles from "./styles";
import { reduxForm } from "redux-form";
import SmartRender from "containers/SmartRender";
import { forms } from "utils/constants";
import { gotoUrl } from "utils/helpers";
import Spinner, { styles as spinnerStyles } from "components/Spinner";
import NotEligible from "components/NotEligible";
import {
  makeSelectSuspensionDetails,
  makeSelectOrderRef,
  makeSelectSuspensionPaymentDetailFormattedAmount,
  makeSelectedFormattedSuspensionStartDate,
  makeSelectedFormattedSuspensionEndDate,
  makeSelectPageValidations,
  makeSelectSuspendNumberFormPayload,
  makeSelectFormattedSuspensionTotalAmount,
  makeSelectCancelSuspendNumberPayload,
  makeSelectSubmitSuspensionDetails,
  makeSelectIsFetchingAnyData,
  makeSelectSessionToken,
  makeSelectOrderDetails
} from "ducks/selectors";
import validate from "validations";

const SuspensionSummary = Component =>
  function Comp(props) {
    const {
      orderRef,
      suspensionDetails,
      selectedSuspensionStartDate,
      selectedSuspensionEndDate,
      isFetchingAnyData,
      sessionToken
    } = props;

    useEffect(
      () => {
        if (selectedSuspensionStartDate && selectedSuspensionEndDate) {
          fetchSuspensionPaymentDetails({
            orderRef,
            startDate: selectedSuspensionStartDate,
            endDate: selectedSuspensionEndDate,
            amount: get(suspensionDetails, "suspensionPricePerMonth")
          });
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [selectedSuspensionStartDate, selectedSuspensionEndDate]
    );

    if (!sessionToken) return <NotEligible />;

    if (isFetchingAnyData)
      return <Spinner size={200} sx={spinnerStyles.fullPage} />;

    return <Component {...props} />;
  };

const mapStateToProps = createStructuredSelector({
  styles: () => styles,
  sessionToken: makeSelectSessionToken(),
  isFetchingAnyData: makeSelectIsFetchingAnyData(),
  formValidations: makeSelectPageValidations(forms.SUSPEND_NUMBER_FORM),
  orderRef: makeSelectOrderRef(),
  suspensionDetails: makeSelectSuspensionDetails(),
  formattedSuspensionStartDate: makeSelectedFormattedSuspensionStartDate(),
  formattedSuspensionEndDate: makeSelectedFormattedSuspensionEndDate(),
  formattedSuspensionAmount: makeSelectSuspensionPaymentDetailFormattedAmount(),
  suspendNumberPayload: makeSelectSuspendNumberFormPayload(),
  formattedSuspensionTotalCharge: makeSelectFormattedSuspensionTotalAmount(),
  cancelSuspendNumberPayload: makeSelectCancelSuspendNumberPayload(),
  submitSuspensionDetails: makeSelectSubmitSuspensionDetails(),
  orderDetails: makeSelectOrderDetails()
});

export function mapDispatchToProps(dispatch) {
  return {
    submitSuspension: (params, callback) => {
      dispatch(submitSuspension(params, callback));
    },
    submitCancelSuspension: (params, callback) => {
      dispatch(submitCancelSuspension(params, callback));
    }
  };
}

export const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  messageData: {
    formattedSuspendNumberStartDate: get(
      stateProps,
      "formattedSuspensionStartDate.label"
    ),
    formattedSuspendNumberEndDate: get(
      stateProps,
      "formattedSuspensionEndDate.label"
    ),
    formattedSuspendNumberChargeTotalPrice: get(
      stateProps,
      "formattedSuspensionAmount.formattedPrice"
    ),
    formattedSuspensionTotalCharge: get(
      stateProps,
      "formattedSuspensionTotalCharge.formattedPrice"
    ),
    errorMessage: get(stateProps, "submitSuspensionDetails.error", ""),
    ineligibleDescription: get(
      stateProps,
      "orderDetails.eligibility.description"
    ),
    ineligibleDisclaimer: get(
      stateProps,
      "orderDetails.eligibility.disclaimer"
    ),
    ineligibleReason: get(stateProps, "orderDetails.eligibility.reason"),
    ineligibleValue: get(stateProps, "orderDetails.eligibility.value")
  },
  submitSuspensionAndNavigate: (navigate, payload, data) => {
    dispatchProps.submitSuspension(
      stateProps.suspendNumberPayload,
      (res, error) => {
        if (get(res, "code") === 0 && isEmpty(error)) {
          navigate();
        } else {
          get(data, "next.errorPath") && gotoUrl(get(data, "next.errorPath"));
        }
      }
    );
  },
  submitCancelSuspendAndNavigate: (navigate, payload) => {
    dispatchProps.submitCancelSuspension(
      stateProps.cancelSuspendNumberPayload,
      (res, error) => {
        if (get(res, "code") === 0 && isEmpty(error)) {
          navigate();
        }
      }
    );
  }
});

const enhance = compose(
  connect(mapStateToProps, mapDispatchToProps, mergeProps),
  reduxForm({
    form: forms.SUSPEND_NUMBER_FORM,
    validate: validate,
    destroyOnUnmount: false
  })
);

export default enhance(SuspensionSummary(SmartRender));
